import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ArrowLeft,
    Settings,
    DollarSign,
    Box,
    FileText,
    Image as ImageIcon,
    CheckCircle2,
    User,
    MapPin,
    Activity,
    Clock,
    Wrench,
    Repeat,
    Plus,
    MoreVertical,
    Download,
    Trash2,
    ArrowDown,
    Calendar as CalendarIcon
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { MaintenanceRecord, Asset } from '../../types/asset';
import { toast } from 'sonner';
import { useAbility } from '@casl/react';
import { AbilityContext } from '../../context/casl/AbilityContext';
import { ACTIONS, RESOURCES } from '../../constants/permissions.constants';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import { cn } from '../../components/ui/utils';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { toSydneyYYYYMMDD, toSydneyDate } from '../../utils/helpers';

const MaintenanceDetails: React.FC = () => {
    const { id, maintenanceId } = useParams<{ id: string, maintenanceId: string }>();
    const navigate = useNavigate();
    const ability = useAbility(AbilityContext);
    const [maintenance, setMaintenance] = useState<MaintenanceRecord | null>(null);
    const [asset, setAsset] = useState<Asset | null>(null);
    const [loading, setLoading] = useState(true);
    const [isImageModalOpen, setIsImageModalOpen] = useState(false);
    const [selectedImage, setSelectedImage] = useState('');
    const { currentUser } = useAuth();
    const [uploadingAttachments, setUploadingAttachments] = useState(false);

    // Delete Modal State
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [attachmentToDelete, setAttachmentToDelete] = useState<string | null>(null);
    const [deletingAttachment, setDeletingAttachment] = useState(false);

    const [deleteRecordModalOpen, setDeleteRecordModalOpen] = useState(false);
    const [deletingRecord, setDeletingRecord] = useState(false);

    // Completion Modal State
    const [completeModalOpen, setCompleteModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState<'edit' | 'complete'>('edit');
    const [completing, setCompleting] = useState(false);
    const [completionData, setCompletionData] = useState({
        completedDate: new Date().toISOString().split('T')[0],
        cost: '',
        description: '',
        setupReminder: false,
        type: '',
        vendor: '',
        performedBy: '',
        scheduledDate: '',
        scheduledTime: ''
    });

    const fetchDetails = useCallback(async () => {
        try {
            setLoading(true);
            const res = await api.get<MaintenanceRecord>(`/api/assets/${id}/maintenance/${maintenanceId}`);
            setMaintenance(res);

            // If asset not in maintenance record, fetch it
            if (!(res as any).asset) {
                const assetRes = await api.get<Asset>(`/api/assets/${id}`);
                setAsset(assetRes);
            } else {
                setAsset((res as any).asset);
            }
        } catch (error) {
            console.error('Error fetching maintenance details:', error);
            toast.error('Failed to load maintenance details');
        } finally {
            setLoading(false);
        }
    }, [id, maintenanceId]);

    useEffect(() => {
        if (id && maintenanceId) {
            fetchDetails();
        }
    }, [id, maintenanceId, fetchDetails]);

    const handleAddAttachments = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || e.target.files.length === 0) return;

        try {
            setUploadingAttachments(true);
            const data = new FormData();
            Array.from(e.target.files).forEach(file => {
                data.append('attachments', file);
            });

            await api.post(`/api/assets/${id}/maintenance/${maintenanceId}/attachments`, data);
            toast.success('Attachments added successfully');
            fetchDetails();
        } catch (error: any) {
            console.error('Error adding attachments:', error);
            toast.error(error.response?.data?.message || 'Failed to add attachments');
        } finally {
            setUploadingAttachments(false);
            e.target.value = '';
        }
    };

    const resetModalForm = (mode: 'edit' | 'complete') => {
        if (!maintenance) return;
        setModalMode(mode);
        setCompletionData({
            completedDate: mode === 'complete'
                ? toSydneyYYYYMMDD(new Date())
                : (maintenance.completedDate ? toSydneyYYYYMMDD(maintenance.completedDate) : ''),
            cost: maintenance.cost ? maintenance.cost.toString() : '',
            description: maintenance.description || '',
            setupReminder: false,
            type: maintenance.type || '',
            vendor: maintenance.vendor || '',
            performedBy: maintenance.performedBy || '',
            scheduledDate: maintenance.scheduledDate ? toSydneyYYYYMMDD(maintenance.scheduledDate) : '',
            scheduledTime: maintenance.scheduledTime || ''
        });
        setCompleteModalOpen(true);
    };

    const handleComplete = async () => {
        try {
            setCompleting(true);
            const isUpdate = modalMode === 'edit';
            const endpoint = isUpdate
                ? `/api/assets/${id}/maintenance/${maintenanceId}`
                : `/api/assets/${id}/maintenance/${maintenanceId}/complete`;

            await api.patch(endpoint, completionData);
            toast.success(isUpdate ? 'Maintenance record updated' : 'Maintenance marked as complete');
            setCompleteModalOpen(false);

            if (!isUpdate && completionData.setupReminder) {
                navigate(`/assets/${id}/reminders/new?prefill=true&name=${encodeURIComponent('Reminder for next estimated inspection date')}&type=Schedule Maintenance`);
            }

            fetchDetails();
        } catch (err: any) {
            toast.error(err.response?.data?.message || 'Failed to update maintenance');
        } finally {
            setCompleting(false);
        }
    };

    const confirmDeleteAttachment = (attachmentId: string) => {
        setAttachmentToDelete(attachmentId);
        setDeleteModalOpen(true);
    };

    const executeDeleteAttachment = async () => {
        if (!attachmentToDelete) return;

        try {
            setDeletingAttachment(true);
            await api.delete(`/api/assets/${id}/maintenance/${maintenanceId}/attachments/${attachmentToDelete}`);
            toast.success('Attachment deleted successfully');
            setDeleteModalOpen(false);
            setAttachmentToDelete(null);
            fetchDetails();
        } catch (error: any) {
            console.error('Error deleting attachment:', error);
            toast.error(error.response?.data?.message || 'Failed to delete attachment');
        } finally {
            setDeletingAttachment(false);
        }
    };

    const handleDeleteRecord = async () => {
        try {
            setDeletingRecord(true);
            await api.delete(`/api/assets/${id}/maintenance/${maintenanceId}`);
            toast.success('Maintenance record deleted successfully');
            navigate(`/assets/${id}`);
        } catch (error: any) {
            console.error('Error deleting maintenance record:', error);
            toast.error(error.response?.data?.message || 'Failed to delete maintenance record');
        } finally {
            setDeletingRecord(false);
            setDeleteRecordModalOpen(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (!maintenance) {
        return (
            <div className="text-center py-20">
                <h2 className="text-2xl font-bold text-gray-900">Maintenance record not found</h2>
                <Button onClick={() => navigate(`/assets/${id}`)} className="mt-4">Back to Asset</Button>
            </div>
        );
    }

    return (
        <div className="max-w-[1000px] mx-auto pb-20 animate-in fade-in duration-500">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                <div className="flex items-center gap-4">
                    <Button
                        variant="ghost"
                        size="icon"
                        className="rounded-xl border border-gray-100 bg-white hover:bg-gray-50 flex-shrink-0"
                        onClick={() => navigate(`/assets/${id}`)}
                    >
                        <ArrowLeft size={20} />
                    </Button>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 tracking-tight flex items-center gap-2">
                            Maintenance Details
                        </h1>
                        <p className="text-gray-500 text-sm font-medium mt-0.5">
                            {maintenance.type} Record #{(maintenance.id as any).toString().padStart(4, '0')}
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    {/* Desktop Actions */}
                    <div className="hidden md:flex items-center gap-2">
                        {ability.can(ACTIONS.UPDATE, RESOURCES.ASSET_MAINTENANCE) && (
                            <Button
                                variant="outline"
                                className="bg-white hover:bg-gray-50 text-gray-700 font-bold rounded-xl px-4"
                                onClick={() => resetModalForm('edit')}
                            >
                                <Settings className="w-4 h-4 mr-2" />
                                Edit Details
                            </Button>
                        )}
                        {ability.can(ACTIONS.DELETE, RESOURCES.ASSET_MAINTENANCE) && (
                            <Button
                                variant="outline"
                                className="bg-white hover:bg-red-50 text-red-600 border-red-100 hover:border-red-200 font-bold rounded-xl px-4"
                                onClick={() => setDeleteRecordModalOpen(true)}
                            >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                            </Button>
                        )}
                        {maintenance.status === 'Scheduled' && ability.can(ACTIONS.COMPLETE, RESOURCES.ASSET_MAINTENANCE) && (
                            <Button
                                className="bg-green-600 hover:bg-green-700 text-white font-bold rounded-xl px-6"
                                onClick={() => resetModalForm('complete')}
                            >
                                <CheckCircle2 className="w-4 h-4 mr-2" />
                                Complete Maintenance
                            </Button>
                        )}
                    </div>

                    {/* Mobile Actions Dropdown */}
                    <div className="md:hidden w-full">
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    variant="outline"
                                    className="flex items-center gap-2 w-full justify-center rounded-xl bg-white border-gray-200 text-gray-700 font-bold"
                                >
                                    <MoreVertical size={18} />
                                    <span>Actions</span>
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56 p-2 rounded-2xl border-gray-100 shadow-xl">
                                {ability.can(ACTIONS.UPDATE, RESOURCES.ASSET_MAINTENANCE) && (
                                    <DropdownMenuItem
                                        className="rounded-xl py-3 font-bold text-gray-700 focus:bg-gray-50 flex items-center gap-2 cursor-pointer"
                                        onClick={() => {
                                            setCompletionData({
                                                completedDate: maintenance.completedDate ? new Date(maintenance.completedDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
                                                cost: maintenance.cost ? maintenance.cost.toString() : '',
                                                description: maintenance.description || '',
                                                setupReminder: false,
                                                type: maintenance.type || '',
                                                vendor: maintenance.vendor || '',
                                                performedBy: maintenance.performedBy || '',
                                                scheduledDate: maintenance.scheduledDate ? new Date(maintenance.scheduledDate).toISOString().split('T')[0] : '',
                                                scheduledTime: maintenance.scheduledTime || ''
                                            });
                                            setModalMode('edit');
                                            setCompleteModalOpen(true);
                                        }}
                                    >
                                        <Settings className="w-4 h-4" />
                                        Edit Details
                                    </DropdownMenuItem>
                                )}
                                {ability.can(ACTIONS.DELETE, RESOURCES.ASSET_MAINTENANCE) && (
                                    <DropdownMenuItem
                                        className="rounded-xl py-3 font-bold text-red-600 focus:bg-red-50 focus:text-red-700 flex items-center gap-2 cursor-pointer"
                                        onClick={() => setDeleteRecordModalOpen(true)}
                                    >
                                        <Trash2 className="w-4 h-4" />
                                        Delete Record
                                    </DropdownMenuItem>
                                )}
                                {maintenance.status === 'Scheduled' && ability.can(ACTIONS.COMPLETE, RESOURCES.ASSET_MAINTENANCE) && (
                                    <DropdownMenuItem
                                        className="rounded-xl py-3 font-bold text-green-600 focus:bg-green-50 focus:text-green-700 flex items-center gap-2 cursor-pointer"
                                        onClick={() => {
                                            completionData.completedDate = new Date().toISOString().split('T')[0];
                                            completionData.setupReminder = false;
                                            setModalMode('complete');
                                            setCompleteModalOpen(true);
                                        }}
                                    >
                                        <CheckCircle2 className="w-4 h-4" />
                                        Complete Maintenance
                                    </DropdownMenuItem>
                                )}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </div>
            </div>

            <div className="space-y-6">
                {/* 1. Maintenance Summary Information */}
                <section className="bg-white p-6 md:p-8 rounded-[32px] border border-gray-100 shadow-sm space-y-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <Settings size={24} className="text-blue-500" />
                            <div>
                                <h2 className="font-bold text-gray-900 text-lg">Maintenance Summary</h2>
                                <p className="text-gray-500 text-xs font-medium">Core service overview</p>
                            </div>
                        </div>
                        <Badge className={cn("px-2.5 py-1 rounded-lg border-0 text-[10px] font-bold uppercase tracking-widest",
                            maintenance.status === 'Completed' ? "bg-green-100 text-green-700" :
                                maintenance.status === 'Scheduled' ? "bg-slate-100 text-slate-700" :
                                    "bg-blue-100 text-blue-700"
                        )}>
                            {maintenance.status}
                        </Badge>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 bg-gray-50 p-6 rounded-2xl border border-gray-100">
                        <div className="space-y-1">
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">Type</span>
                            <div className="flex items-center gap-2 font-bold text-gray-900">
                                <Activity size={14} className="text-blue-500" />
                                {maintenance.type}
                            </div>
                        </div>
                        <div className="space-y-1">
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">Scheduled Date</span>
                            <div className="flex items-center gap-2 font-bold text-gray-900">
                                <CalendarIcon size={14} className="text-orange-500" />
                                {maintenance.scheduledDate ? toSydneyDate(maintenance.scheduledDate) : '-'}
                            </div>
                        </div>
                        {maintenance.completedDate && (
                            <div className="space-y-1">
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">Completed Date</span>
                                <div className="flex items-center gap-2 font-bold text-gray-900">
                                    <CheckCircle2 size={14} className="text-green-500" />
                                    {maintenance.completedDate ? toSydneyDate(maintenance.completedDate) : '-'}
                                </div>
                            </div>
                        )}
                        <div className="space-y-1">
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">Cost</span>
                            <div className="flex items-center gap-2 font-bold text-gray-900">
                                <DollarSign size={14} className="text-blue-500" />
                                {Number(maintenance.cost || 0).toLocaleString()}
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 pt-4">
                        <div className="space-y-1">
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">Vendor</span>
                            <div className="flex items-center gap-2 font-bold text-gray-900">
                                <Box size={16} className="text-indigo-500" />
                                {maintenance.vendor || 'N/A'}
                            </div>
                        </div>
                        <div className="space-y-1">
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">Technician / Performed By</span>
                            <div className="flex items-center gap-2 font-bold text-gray-900">
                                <User size={16} className="text-blue-500" />
                                {maintenance.performedBy || 'Unknown'}
                            </div>
                        </div>
                        <div className="space-y-1">
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">Location</span>
                            <div className="flex items-center gap-2 font-bold text-gray-900">
                                <MapPin size={16} className="text-red-500" />
                                {maintenance.location || 'N/A'}
                            </div>
                        </div>
                        {maintenance.recurrenceInterval && (
                            <div className="space-y-1">
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">Recurrence</span>
                                <div className="flex items-center gap-2 font-bold text-gray-900">
                                    <Repeat size={16} className="text-orange-500" />
                                    {maintenance.recurrenceInterval}
                                </div>
                            </div>
                        )}
                        {maintenance.odometerReading && (
                            <div className="space-y-1">
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">Odometer Reading</span>
                                <div className="flex items-center gap-2 font-bold text-gray-900">
                                    <Activity size={16} className="text-slate-500" />
                                    {Number(maintenance.odometerReading).toLocaleString()} km
                                </div>
                            </div>
                        )}
                        <div className="space-y-1">
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">Logged By</span>
                            <div className="flex items-center gap-2 font-bold text-gray-900">
                                <User size={16} className="text-indigo-500" />
                                {maintenance.createdBy?.name || 'System'}
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-gray-100">
                        {maintenance.partsReplaced && (
                            <div>
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-2">Parts Replaced</span>
                                <div className="flex flex-wrap gap-2">
                                    {maintenance.partsReplaced.split(',').map((part, idx) => (
                                        <Badge key={idx} variant="outline" className="bg-gray-50 border-gray-200 text-gray-700 font-bold px-3 py-1 rounded-xl">
                                            {part.trim()}
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                        )}
                        {maintenance.description && (
                            <div>
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-2">Description</span>
                                <p className="text-sm font-medium text-gray-600 leading-relaxed bg-gray-50 p-4 rounded-xl border border-gray-100">
                                    {maintenance.description}
                                </p>
                            </div>
                        )}
                    </div>
                </section>

                {/* 2. Maintenance Attachments */}
                <section className="bg-white p-6 md:p-8 rounded-[32px] border border-gray-100 shadow-sm space-y-6 animate-in slide-in-from-bottom-4 duration-500">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <ImageIcon size={24} className="text-indigo-500" />
                            <div>
                                <h2 className="font-bold text-gray-900 text-lg">Images & Documents</h2>
                                <p className="text-gray-500 text-xs font-medium">Visual proof of maintenance</p>
                            </div>
                        </div>
                        <div>
                            <input
                                type="file"
                                multiple
                                id="add-attachments"
                                className="hidden"
                                onChange={handleAddAttachments}
                                disabled={uploadingAttachments}
                                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                            />
                            <label htmlFor="add-attachments">
                                <Button
                                    asChild
                                    variant="outline"
                                    className="rounded-xl border-gray-200 hover:bg-gray-50 font-bold cursor-pointer"
                                    disabled={uploadingAttachments}
                                >
                                    <span>
                                        {uploadingAttachments ? (
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600 mr-2 md:mr-2"></div>
                                        ) : (
                                            <Plus size={16} className={cn(uploadingAttachments ? "" : "md:mr-2")} />
                                        )}
                                        <span className="hidden md:inline">
                                            {uploadingAttachments ? 'Uploading...' : 'Add Additional Attachments'}
                                        </span>
                                    </span>
                                </Button>
                            </label>
                        </div>
                    </div>

                    {maintenance.attachments && maintenance.attachments.length > 0 ? (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-indigo-50/30 p-6 rounded-2xl border border-indigo-50/50">
                            {maintenance.attachments.map((att: any, idx: number) => {
                                const isImage = att.mimetype.startsWith('image/');
                                return (
                                    <div
                                        key={idx}
                                        className={cn("rounded-2xl overflow-hidden border border-gray-200 group relative bg-white", isImage ? "aspect-square cursor-pointer" : "p-4 flex flex-col items-center justify-center h-full cursor-pointer")}
                                        onClick={() => {
                                            if (isImage) {
                                                setSelectedImage(att.url);
                                                setIsImageModalOpen(true);
                                            } else {
                                                window.open(att.url, '_blank');
                                            }
                                        }}
                                    >
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                confirmDeleteAttachment(att.id);
                                            }}
                                            className="absolute top-2 right-2 p-1.5 bg-red-500 hover:bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 z-20 shadow-sm"
                                            title="Delete attachment"
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                        {isImage ? (
                                            <>
                                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors z-10 flex items-center justify-center">
                                                    <div className="opacity-0 group-hover:opacity-100 bg-white/90 rounded-full p-2 transform scale-75 group-hover:scale-100 transition-all duration-300">
                                                        <ImageIcon size={20} className="text-gray-700" />
                                                    </div>
                                                </div>
                                                <img src={att.url} alt={att.filename} className="w-full h-full object-cover" />
                                            </>
                                        ) : (
                                            <>
                                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors z-10 flex items-center justify-center">
                                                    <div className="opacity-0 group-hover:opacity-100 bg-white/90 rounded-full p-2 transform scale-75 group-hover:scale-100 transition-all duration-300">
                                                        <Download size={20} className="text-gray-700" />
                                                    </div>
                                                </div>
                                                <FileText size={32} className="text-gray-400 mb-2" />
                                                <span className="text-xs font-medium text-gray-600 text-center truncate w-full px-2" title={att.filename}>{att.filename}</span>
                                            </>
                                        )}
                                    </div>
                                )
                            })}
                        </div>
                    ) : (
                        <div className="text-center py-8 bg-gray-50 rounded-2xl border border-gray-100 border-dashed">
                            <ImageIcon size={32} className="text-gray-300 mx-auto mb-3" />
                            <h3 className="text-sm font-bold text-gray-900">No attachments yet</h3>
                            <p className="text-xs font-medium text-gray-500 mt-1">Upload images or documents related to this maintenance.</p>
                        </div>
                    )}
                </section>
            </div>

            {/* Image Modal */}
            <Dialog open={isImageModalOpen} onOpenChange={setIsImageModalOpen}>
                <DialogContent className="max-w-[90vw] max-h-[90vh] p-0 overflow-hidden bg-transparent border-none shadow-none flex items-center justify-center">
                    <img
                        src={selectedImage}
                        alt="Maintenance Attachment"
                        className="w-full h-auto max-h-[90vh] object-contain rounded-2xl ring-1 ring-white/10 shadow-2xl"
                    />
                </DialogContent>
            </Dialog>

            {/* Delete Modal */}
            <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
                <DialogContent className="max-w-[400px] rounded-[32px] p-8 border-gray-100">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-bold text-gray-900 tracking-tight flex items-center gap-2">
                            <Trash2 className="text-red-500" />
                            Delete Attachment
                        </DialogTitle>
                    </DialogHeader>
                    <div className="py-2">
                        <p className="text-gray-600 text-sm font-medium">
                            Are you sure you want to delete this attachment? This action cannot be undone.
                        </p>
                    </div>
                    <DialogFooter className="flex-col sm:flex-row gap-2 mt-2">
                        <Button variant="ghost" className="rounded-2xl h-12 font-bold flex-1" onClick={() => setDeleteModalOpen(false)}>Cancel</Button>
                        <Button
                            className="rounded-2xl h-12 bg-red-600 hover:bg-red-700 text-white font-bold flex-1"
                            disabled={deletingAttachment}
                            onClick={executeDeleteAttachment}
                        >
                            {deletingAttachment ? 'Deleting...' : 'Delete'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Record Modal */}
            <Dialog open={deleteRecordModalOpen} onOpenChange={setDeleteRecordModalOpen}>
                <DialogContent className="max-w-[400px] rounded-[32px] p-8 border-gray-100">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-bold text-gray-900 tracking-tight flex items-center gap-2">
                            <Trash2 className="text-red-500" />
                            Delete Maintenance Record
                        </DialogTitle>
                    </DialogHeader>
                    <div className="py-2">
                        <p className="text-gray-600 text-sm font-medium">
                            Are you sure you want to delete this maintenance record? This action cannot be undone and will be removed from the asset history.
                        </p>
                    </div>
                    <DialogFooter className="flex-col sm:flex-row gap-2 mt-2">
                        <Button variant="ghost" className="rounded-2xl h-12 font-bold flex-1" onClick={() => setDeleteRecordModalOpen(false)}>Cancel</Button>
                        <Button
                            className="rounded-2xl h-12 bg-red-600 hover:bg-red-700 text-white font-bold flex-1"
                            disabled={deletingRecord}
                            onClick={handleDeleteRecord}
                        >
                            {deletingRecord ? 'Deleting...' : 'Delete'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Completion Modal */}
            <Dialog open={completeModalOpen} onOpenChange={setCompleteModalOpen}>
                <DialogContent className="max-w-[500px] rounded-[32px] p-8 border-gray-100">
                    <DialogHeader>
                        <DialogTitle className="text-2xl font-bold text-gray-900 tracking-tight flex items-center gap-2">
                            {modalMode === 'edit' ? <Settings className="text-blue-500" /> : <CheckCircle2 className="text-green-500" />}
                            {modalMode === 'edit' ? 'Edit Maintenance Details' : 'Complete Maintenance'}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto pr-2">
                        <div className="space-y-2">
                            <label className="text-xs font-bold text-gray-900 uppercase tracking-widest">Type</label>
                            <select
                                className="flex h-12 w-full rounded-2xl border-gray-100 bg-gray-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                value={completionData.type}
                                onChange={(e) => setCompletionData({ ...completionData, type: e.target.value })}
                            >
                                <option value="">Select type...</option>
                                {[
                                    'Oil Change', 'Major Service', 'Brake Check', 'Routine Inspection',
                                    'Hardware Cleaning', 'Software Update', 'Battery Replacement',
                                    'Tyre Rotation', 'Fluid Check', 'Emergency Repair',
                                    'Preventive Maintenance', 'Calibration', 'Safety Inspection'
                                ].map(t => <option key={t} value={t}>{t}</option>)}
                            </select>
                        </div>
                        <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-900 uppercase tracking-widest">Scheduled Date & Time</label>
                                <div className="grid grid-cols-2 gap-2">
                                    <Input
                                        type="date"
                                        max="9999-12-31"
                                        className="rounded-2xl border-gray-100 bg-gray-50 h-12 px-4"
                                        value={completionData.scheduledDate}
                                        onChange={(e) => setCompletionData({ ...completionData, scheduledDate: e.target.value })}
                                    />
                                    <Input
                                        type="time"
                                        className="rounded-2xl border-gray-100 bg-gray-50 h-12 px-4"
                                        value={completionData.scheduledTime}
                                        onChange={(e) => setCompletionData({ ...completionData, scheduledTime: e.target.value })}
                                    />
                                </div>
                            </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {(modalMode === 'complete' || maintenance.status === 'Completed') && (
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-gray-900 uppercase tracking-widest">Completion Date</label>
                                    <Input
                                        type="date"
                                        max="9999-12-31"
                                        className="rounded-2xl border-gray-100 bg-gray-50 h-12 px-4"
                                        value={completionData.completedDate}
                                        onChange={(e) => setCompletionData({ ...completionData, completedDate: e.target.value })}
                                    />
                                </div>
                            )}
                            <div className={cn("space-y-2", (modalMode !== 'complete' && maintenance.status !== 'Completed') && "col-span-2")}>
                                <label className="text-xs font-bold text-gray-900 uppercase tracking-widest">Final Cost</label>
                                <div className="relative">
                                    <DollarSign size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                                    <Input
                                        type="number"
                                        placeholder="0.00"
                                        className="rounded-2xl border-gray-100 bg-gray-50 h-12 pl-9"
                                        value={completionData.cost}
                                        onChange={(e) => setCompletionData({ ...completionData, cost: e.target.value })}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-900 uppercase tracking-widest">Vendor</label>
                                <Input
                                    placeholder="Service Provider"
                                    className="rounded-2xl border-gray-100 bg-gray-50 h-12"
                                    value={completionData.vendor}
                                    onChange={(e) => setCompletionData({ ...completionData, vendor: e.target.value })}
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-900 uppercase tracking-widest">Performed By</label>
                                <Input
                                    placeholder="Technician/Staff"
                                    className="rounded-2xl border-gray-100 bg-gray-50 h-12"
                                    value={completionData.performedBy}
                                    onChange={(e) => setCompletionData({ ...completionData, performedBy: e.target.value })}
                                />
                            </div>
                        </div>

                        {(modalMode === 'complete' || maintenance.status === 'Completed') && (
                            <div className="flex items-start gap-3 p-4 bg-blue-50/50 rounded-2xl border border-blue-100 shadow-sm animate-in fade-in slide-in-from-bottom-2 duration-300">
                                <Activity className="text-blue-500 shrink-0 mt-0.5" size={18} />
                                <div className="flex-1">
                                    <div className="flex items-center justify-between">
                                        <label className="text-sm font-bold text-gray-900 leading-none">Setup reminder for next Scheduled Maintenance</label>
                                        <Switch
                                            checked={completionData.setupReminder}
                                            onCheckedChange={(checked) => setCompletionData({ ...completionData, setupReminder: checked })}
                                        />
                                    </div>
                                    <p className="text-[10px] font-medium text-gray-500 mt-2">
                                        Turn this on to set up reminder notifications once the record is saved. You'll be taken to the Reminders page automatically.
                                    </p>
                                </div>
                            </div>
                        )}

                        <div className="space-y-2">
                            <label className="text-xs font-bold text-gray-900 uppercase tracking-widest">Description</label>
                            <Textarea
                                className="rounded-2xl border-gray-100 bg-gray-50 min-h-[100px] p-4 text-sm font-medium"
                                placeholder="Details of work performed..."
                                value={completionData.description}
                                onChange={(e) => setCompletionData({ ...completionData, description: e.target.value })}
                            />
                        </div>
                    </div>
                    <DialogFooter className="flex-col sm:flex-row gap-2 mt-2">
                        <Button variant="ghost" className="rounded-2xl h-12 font-bold flex-1" onClick={() => setCompleteModalOpen(false)}>Cancel</Button>
                        <Button
                            className="rounded-2xl h-12 bg-blue-600 hover:bg-blue-700 text-white font-bold flex-1"
                            disabled={completing || (modalMode === 'complete' && !completionData.completedDate)}
                            onClick={handleComplete}
                        >
                            {completing ? 'Saving...' : (modalMode === 'edit' ? 'Update Details' : 'Complete Maintenance')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default MaintenanceDetails;
